import { notifyManager } from "@tanstack/react-query";
import { ApiCompetitionBulkAttemptData } from "griptonite-api-client";

import api from "@/core/api";
import queryClient from "@/core/queryClient";

/**
 * Maximum number of user UUIDs to include in a single batched request.
 *
 * When the pending set hits this size, the batch is flushed immediately
 * rather than waiting for the debounce window to expire. This prevents
 * excessively long query strings and keeps individual requests manageable
 * when a large number of rows become visible at once (e.g. fast scroll).
 */
const MAX_BATCH_SIZE = 100;

/**
 * How long (ms) to wait after the last `fetch()` call before flushing the
 * accumulated batch. Gives time for all rows rendered in the same paint to
 * register their requests before a single API call is fired.
 */
const DEBOUNCE_MS = 500;

export type AttemptsBatcher = {
  /**
   * Request attempts data for a single user. Calls that arrive within
   * {@link DEBOUNCE_MS} of each other (or that fill a batch up to
   * {@link MAX_BATCH_SIZE}) are coalesced into one API request.
   */
  fetch: (userUuid: string) => Promise<ApiCompetitionBulkAttemptData[]>;
};

/**
 * Creates a batcher for competition attempt data that coalesces multiple
 * per-user requests into a single `getCompetitionAttempts` API call.
 *
 * Inspired by [@yornaath/batshit](https://github.com/yornaath/batshit) but
 * simplified and typed specifically for this use case.
 *
 * ### How it works
 * Each call to `fetch(userUuid)` adds the UUID to a pending set and arms a
 * debounce timer. When the timer fires (or the pending set reaches
 * {@link MAX_BATCH_SIZE}), all accumulated UUIDs are sent in one request and
 * each caller's promise is resolved with their slice of the response.
 *
 * ### Usage
 * One batcher instance should be shared per competition (see `getAttemptsBatcher`
 * in `src/hooks/competitions.ts`). Pass `batcher.fetch` as the `queryFn` of a
 * React Query `useQuery` call — React Query handles caching and deduplication on
 * top of the batcher's request coalescing.
 */
export function createAttemptsBatcher(competitionUuid: string): AttemptsBatcher {
  let pending = new Set<string>();
  let resolvers = new Map<string, (data: ApiCompetitionBulkAttemptData[]) => void>();
  let rejecters = new Map<string, (err: unknown) => void>();
  let timer: ReturnType<typeof setTimeout> | null = null;

  const flush = async () => {
    timer = null;
    const batch = [...pending];
    const batchResolvers = new Map(resolvers);
    const batchRejecters = new Map(rejecters);
    pending = new Set();
    resolvers = new Map();
    rejecters = new Map();

    try {
      const { attempts } = await api.competitions.getCompetitionAttempts(competitionUuid, {
        userUuids: batch,
      });
      const byUser: Record<string, ApiCompetitionBulkAttemptData[]> = {};
      attempts.forEach((a) => (byUser[a.user] ??= []).push(a));

      // Wrap all cache writes in notifyManager.batch() so React Query defers
      // every observer notification until the callback completes, then flushes
      // them all synchronously. Without this, each setQueryData schedules its
      // own microtask notification, resulting in one React render per user.
      notifyManager.batch(() => {
        batch.forEach((uuid) => {
          const attempts = byUser[uuid] ?? [];
          queryClient.setQueryData(
            ["competitions", competitionUuid, "attempts", "user", uuid],
            attempts,
          );
          // Resolve each promise so React Query considers the queryFn complete.
          // The cache is already up-to-date, so these are no-ops render-wise.
          batchResolvers.get(uuid)?.(attempts);
        });
      });
    } catch (err) {
      batch.forEach((uuid) => batchRejecters.get(uuid)?.(err));
    }
  };

  return {
    fetch(userUuid) {
      return new Promise((resolve, reject) => {
        pending.add(userUuid);
        resolvers.set(userUuid, resolve);
        rejecters.set(userUuid, reject);

        // Flush immediately if we've hit the maximum batch size, otherwise
        // (re)arm the debounce timer so late arrivals can still be coalesced.
        if (pending.size >= MAX_BATCH_SIZE) {
          if (timer) {
            clearTimeout(timer);
            timer = null;
          }
          flush();
        } else {
          if (timer) clearTimeout(timer);
          timer = setTimeout(flush, DEBOUNCE_MS);
        }
      });
    },
  };
}
